// Debt endpoints: CRUD, payment reconciliation, balance/valuation history, and
// statement upload/download.

import { apiFetch } from './client';
import type { Allocation, Debt, DebtView, PaymentSuggestion, Valuation } from './types';

// List all debts with their legacy summary.
export async function getDebts(): Promise<DebtView[]> {
	return apiFetch<DebtView[]>('/debts');
}

// Get one debt with its full detail (charges, payments, chart series).
export async function getDebt(id: string): Promise<DebtView> {
	return apiFetch<DebtView>(`/debts/${encodeURIComponent(id)}`);
}

// Create a debt; the server generates the id.
export async function createDebt(debt: Partial<Debt> & { name: string }): Promise<Debt> {
	return apiFetch<Debt>('/debts', {
		method: 'POST',
		body: JSON.stringify(debt),
	});
}

// Update a debt's config (partial).
export async function updateDebt(id: string, updates: Partial<Debt>): Promise<Debt> {
	return apiFetch<Debt>(`/debts/${encodeURIComponent(id)}`, {
		method: 'PATCH',
		body: JSON.stringify(updates),
	});
}

// Delete a debt.
export async function deleteDebt(id: string): Promise<void> {
	await apiFetch(`/debts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Suggested allocations for reconciling a payment (clears oldest charges first,
// remainder to legacy).
export async function getDebtPaymentSuggestion(
	id: string,
	txPk: string,
	txSk: string
): Promise<PaymentSuggestion> {
	const q = `txPk=${encodeURIComponent(txPk)}&txSk=${encodeURIComponent(txSk)}`;
	return apiFetch<PaymentSuggestion>(`/debts/${encodeURIComponent(id)}/payment-suggestion?${q}`);
}

// Reconcile a payment: write its allocations + role; the API derives the budget split.
export async function reconcilePayment(
	id: string,
	txPk: string,
	txSk: string,
	allocations: Allocation[],
	role?: string
): Promise<void> {
	const path = `/debts/${encodeURIComponent(id)}/payments/${encodeURIComponent(txPk)}/${encodeURIComponent(txSk)}`;
	await apiFetch(path, {
		method: 'PUT',
		body: JSON.stringify({ allocations, role }),
	});
}

// Remove a payment's reconciliation, restoring it to a plain transfer.
export async function unreconcilePayment(id: string, txPk: string, txSk: string): Promise<void> {
	const path = `/debts/${encodeURIComponent(id)}/payments/${encodeURIComponent(txPk)}/${encodeURIComponent(txSk)}`;
	await apiFetch(path, { method: 'DELETE' });
}

// Record a manual balance reconciliation for a facility (corrects feed drift).
// `date` may be backdated to a statement date; an optional statement PDF can be
// attached via statementKey/statementName (see uploadStatement).
export async function reconcileBalance(
	id: string,
	balance: number,
	opts: { note?: string; date?: string; statementKey?: string; statementName?: string } = {}
): Promise<void> {
	await apiFetch(`/debts/${encodeURIComponent(id)}/reconcile`, {
		method: 'PUT',
		body: JSON.stringify({ balance, ...opts }),
	});
}

// Delete a reconcile entry (and its attached statement, server-side).
export async function deleteReconcile(id: string, date: string): Promise<void> {
	await apiFetch(`/debts/${encodeURIComponent(id)}/reconcile/${encodeURIComponent(date)}`, {
		method: 'DELETE',
	});
}

// Save (or upsert) a property valuation for a mortgage debt. Returns the
// updated history (newest first) so the caller can refresh local state.
export async function saveValuation(
	id: string,
	amount: number,
	opts: { date?: string; note?: string } = {}
): Promise<Valuation[]> {
	return apiFetch<Valuation[]>(`/debts/${encodeURIComponent(id)}/valuations`, {
		method: 'POST',
		body: JSON.stringify({ amount, ...opts }),
	});
}

// Delete one valuation history entry by its date.
export async function deleteValuation(id: string, date: string): Promise<void> {
	await apiFetch(`/debts/${encodeURIComponent(id)}/valuations/${encodeURIComponent(date)}`, {
		method: 'DELETE',
	});
}

// Get a short-lived presigned URL to download a statement PDF.
export async function getStatementDownloadUrl(id: string, key: string): Promise<string> {
	const res = await apiFetch<{ downloadUrl: string }>(
		`/debts/${encodeURIComponent(id)}/statements/download?key=${encodeURIComponent(key)}`
	);
	return res.downloadUrl;
}

// Upload a statement PDF: ask the API for a presigned PUT, send the file straight
// to S3, and return the stored key + display name to attach to a reconcile entry.
export async function uploadStatement(
	id: string,
	file: File
): Promise<{ key: string; name: string }> {
	const presign = await apiFetch<{ uploadUrl: string; key: string; name: string }>(
		`/debts/${encodeURIComponent(id)}/statements`,
		{
			method: 'POST',
			body: JSON.stringify({ filename: file.name, contentType: file.type || 'application/pdf' }),
		}
	);
	const put = await fetch(presign.uploadUrl, {
		method: 'PUT',
		body: file,
		headers: { 'Content-Type': file.type || 'application/pdf' },
	});
	if (!put.ok) {
		throw new Error(`Statement upload failed (${put.status})`);
	}
	return { key: presign.key, name: presign.name };
}
